import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Plus,
  Edit,
  Trash2,
  Users,
  Building2,
  Mail,
  Loader2,
} from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { useToast } from "@/hooks/use-toast";
import {
  listClients,
  deleteClient,
  type Client,
} from "@/services/clientsService";
import { ClientForm } from "./ClientForm";
import { usePermissions } from "@/hooks/usePermissions";
import { PermissionGuard } from "@/components/auth/PermissionGuard";
import { PlanStatusBadge } from "@/components/PlanStatusBadge";

interface ClientsManagementProps {
  onBack: () => void;
}

type ViewMode = "list" | "form";

export const ClientsManagement = ({ onBack }: ClientsManagementProps) => {
  const [clients, setClients] = useState<Client[]>([]);
  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [clientToDelete, setClientToDelete] = useState<Client | null>(null);
  const [deleting, setDeleting] = useState(false);
  const { toast } = useToast();
  const { canCreate, canUpdate, canDelete } = usePermissions();

  // Carregar dados iniciais
  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    setLoading(true);
    try {
      const clientsRes = await listClients({ limit: 100 });

      if (clientsRes.success && clientsRes.data) {
        setClients(clientsRes.data.clients);
      }
    } catch (error) {
      toast({
        title: "Erro ao carregar dados",
        description:
          error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveClient = async () => {
    await loadClients();
    setViewMode("list");
    setEditingClient(null);
  };

  const handleNewClient = () => {
    setEditingClient(null);
    setViewMode("form");
  };

  const handleEdit = (client: Client) => {
    setEditingClient(client);
    setViewMode("form");
  };

  const handleDelete = async () => {
    if (!clientToDelete) return;

    setDeleting(true);
    try {
      const response = await deleteClient(clientToDelete.id);

      if (response.success) {
        setClients(clients.filter((c) => c.id !== clientToDelete.id));
        setClientToDelete(null);
        toast({
          title: "Sucesso",
          description: "Cliente e dados vinculados removidos com sucesso",
        });
      } else {
        throw new Error(response.error?.message || "Erro ao remover cliente");
      }
    } catch (error) {
      toast({
        title: "Erro",
        description:
          error instanceof Error ? error.message : "Erro ao remover cliente",
        variant: "destructive",
      });
    } finally {
      setDeleting(false);
    }
  };

  const getStatusBadgeColor = (statusName: string) => {
    const lowerStatus = statusName.toLowerCase();
    if (lowerStatus === "ativo") return "bg-green-500/10 text-green-600";
    if (lowerStatus === "inativo") return "bg-gray-500/10 text-gray-600";
    if (lowerStatus === "suspenso") return "bg-red-500/10 text-red-600";
    if (lowerStatus === "em teste") return "bg-blue-500/10 text-blue-600";
    if (lowerStatus === "cancelado") return "bg-orange-500/10 text-orange-600";
    return "bg-gray-500/10 text-gray-600";
  };

  // Filtrar clientes por busca
  const filteredClients = clients.filter((client) => {
    const search = searchTerm.toLowerCase();
    return (
      client.name.toLowerCase().includes(search) ||
      client.email.toLowerCase().includes(search) ||
      client.company.toLowerCase().includes(search) ||
      client.description?.toLowerCase().includes(search)
    );
  });

  if (viewMode === "form") {
    return (
      <ClientForm
        onBack={() => setViewMode("list")}
        onSave={handleSaveClient}
        editingClient={editingClient}
      />
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-muted flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Carregando clientes...</p>
        </div>
      </div>
    );
  }

  return (
    <PermissionGuard
      permission="res_clients.read"
      fallback={
        <div className="min-h-screen flex items-center justify-center p-8">
          <Card className="max-w-md w-full">
            <CardHeader>
              <CardTitle>Acesso negado</CardTitle>
              <CardDescription>Você não tem permissão para visualizar clientes.</CardDescription>
            </CardHeader>
          </Card>
        </div>
      }
    >
    <div className="min-h-screen bg-gradient-to-br from-background to-muted">
      <PageHeader
        icon={Users}
        title="Cadastro de Clientes"
        description="Gerencie clientes e acesso aos especialistas"
        onBack={onBack}
        actions={
          canCreate("clients") && (
            <Button
              className="bg-gradient-primary text-white hover:opacity-90"
              onClick={handleNewClient}
            >
              <Plus className="h-4 w-4 mr-2" />
              Novo Cliente
            </Button>
          )
        }
      />

      {/* Main Content */}
      <main className="container mx-auto px-6 py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total de Clientes
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{clients.length}</div>
              <p className="text-xs text-muted-foreground mt-1">
                clientes cadastrados
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Clientes Ativos
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">
                {clients.filter((c) => c.status_name === "Ativo").length}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                com acesso ativo
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Especialistas em Uso
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">
                {clients.reduce((acc, c) => acc + c.product_count, 0)}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                especialistas utilizados
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex items-center space-x-2">
              <Input
                placeholder="Buscar por nome, email ou empresa..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1"
              />
            </div>
          </CardContent>
        </Card>

        {/* Clients Table */}
        <Card>
          <CardHeader>
            <CardTitle>Lista de Clientes</CardTitle>
            <CardDescription>
              Gerencie os clientes cadastrados e seus acessos aos especialistas
            </CardDescription>
          </CardHeader>
          <CardContent>
            {filteredClients.length === 0 ? (
              <div className="text-center py-12">
                <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">
                  Nenhum cliente encontrado
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  {searchTerm
                    ? "Nenhum cliente corresponde à sua busca"
                    : "Comece criando seu primeiro cliente"}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Cliente</TableHead>
                      <TableHead>Empresa</TableHead>
                      <TableHead>Plano</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Especialistas</TableHead>
                      <TableHead>Cadastrado em</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredClients.map((client) => (
                      <TableRow key={client.id}>
                        <TableCell>
                          <div>
                            <div className="font-medium">{client.name}</div>
                            <div className="text-sm text-muted-foreground flex items-center">
                              <Mail className="h-3 w-3 mr-1" />
                              {client.email}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center">
                            <Building2 className="h-4 w-4 mr-2 text-muted-foreground" />
                            {client.company}
                          </div>
                        </TableCell>
                        <TableCell>
                          <PlanStatusBadge client={client} />
                        </TableCell>
                        <TableCell>
                          <Badge
                            className={getStatusBadgeColor(client.status_name)}
                          >
                            {client.status_name}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            {client.product_count} especialista
                            {client.product_count !== 1 ? "s" : ""}
                          </div>
                        </TableCell>
                        <TableCell>
                          {new Date(client.created_at).toLocaleDateString(
                            "pt-BR"
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end space-x-2">
                            {canUpdate("clients") && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleEdit(client)}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                            )}
                            {canDelete("clients") && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setClientToDelete(client)}
                                className="text-destructive hover:text-destructive"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>

      <AlertDialog
        open={!!clientToDelete}
        onOpenChange={(open) => {
          if (!open && !deleting) setClientToDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir cliente permanentemente?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-muted-foreground">
                <p>
                  Esta ação é <strong className="text-foreground">irreversível</strong>.
                  Ao excluir{" "}
                  <strong className="text-foreground">
                    {clientToDelete?.company || clientToDelete?.name}
                  </strong>
                  , serão removidos em definitivo:
                </p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Usuários vinculados exclusivamente a este cliente</li>
                  <li>Workspaces do cliente</li>
                  <li>Especialistas (produtos) e agentes exclusivos do cliente</li>
                  <li>Histórico de chamadas de API e demais dados vinculados</li>
                </ul>
                <p>
                  Usuários que também pertencem a outros clientes não serão
                  excluídos, apenas o vínculo com este cliente será removido.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void handleDelete();
              }}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Excluindo...
                </>
              ) : (
                "Excluir permanentemente"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PermissionGuard>
  );
};

